import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { getAnalysis, getAnalysisStatus, AnalysisResult } from '../lib/api';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { AudioPlayer } from '../components/AudioPlayer';
import { Waveform } from '../components/Waveform';
import { SectionInsights } from '../components/SectionInsights';
import { AIChat } from '../components/AIChat';
import { AILoader } from '../components/ui/ai-loader';
import { AnalysisTrivia } from '../components/AnalysisTrivia';
import { MultiStepLoader } from '../components/ui/multi-step-loader';
import { SectionEditorModal } from '../components/SectionEditorModal';
import { DJSuggestions } from '../components/DJSuggestions';
import { SongSnapshot } from '../components/SongSnapshot';
import { TrackDetailCards } from '../components/TrackDetailCards';
import { classifySections, getSectionLabel, SectionType, Section, Segment as ClassifierSegment } from '../utils/sectionClassifier';
import { useAudioPeaks } from '../hooks/useAudioPeaks';
import { useSectionOverrides } from '../hooks/useSectionOverrides';
import { toCamelot } from '../utils/camelot';
import './ResultsPage.css';

const SECTION_GLYPH: Record<SectionType, string> = {
  'mix-in':    'M',
  'intro':     'I',
  'verse':     'V',
  'chorus':    'C',
  'bridge':    'B',
  'breakdown': 'K',
  'main-drop': 'D',
  'build':     'U',
  'outro':     'O',
  'mix-out':   'X',
};


export default function ResultsPage() {
  const { analysisId } = useParams<{ analysisId: string }>();
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [rawSegments, setRawSegments] = useState<ClassifierSegment[]>([]);
  const [loopRegion, setLoopRegion] = useState<{ start: number; end: number } | null>(null);
  const [playSignal, setPlaySignal] = useState(0);

  // Pro = compact (default); Ultimate = expanded. Persisted globally so the
  // pick survives navigation. Tier gating will wrap this control later when
  // subscriptions are wired up.
  const [viewModePref, setViewModePref] = useState<'compact' | 'expanded'>(() => {
    const saved = typeof window !== 'undefined' && window.localStorage.getItem('audioagent.viewMode');
    return saved === 'expanded' ? 'expanded' : 'compact';
  });

  // Expanded is desktop-only — the per-section detail cards + section editor
  // are too dense and dragging-heavy for a phone. We track a viewport flag
  // and force compact while it's true.
  const [isNarrow, setIsNarrow] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 767px)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(max-width: 767px)');
    const onChange = (e: MediaQueryListEvent) => setIsNarrow(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const viewMode: 'compact' | 'expanded' = isNarrow ? 'compact' : viewModePref;
  const setViewMode = (m: 'compact' | 'expanded') => {
    if (isNarrow && m === 'expanded') return; // tab is disabled on mobile
    setViewModePref(m);
  };

  useEffect(() => {
    window.localStorage.setItem('audioagent.viewMode', viewModePref);
  }, [viewModePref]);

  // Decode the audio file to a high-resolution amplitude series and use the
  // low-band (kick channel) to anchor drop / breakdown detection. This is far
  // more accurate than relying on the worker's per-segment energy values.
  const { data: audioPeaks } = useAudioPeaks(audioUrl);

  // User corrections — persisted via append-only JSONL on the API. Applied
  // on top of the classifier output so we capture training data without
  // mutating any classifier internals.
  const { overrides, inserts, submitType, submitBoundary, submitInsert, editInsert, removeInsert, clearAll } = useSectionOverrides(analysisId);
  const [editorOpen, setEditorOpen] = useState(false);

  // Re-classify whenever the segment data, peak series, genre, or override
  // map changes.
  const sections: Section[] = useMemo(() => {
    if (rawSegments.length === 0) return [];
    const classified = classifySections(rawSegments, {
      peakSeries: audioPeaks?.peaks,
      lowSeries: audioPeaks?.low,
      totalDuration: audioPeaks?.duration ?? analysis?.durationSeconds,
      genreFamily: analysis?.genre?.family,
    });
    // Apply user corrections in two passes:
    //   1. Updates: shallow-merge {type, startTime, endTime} into existing
    //      classifier-emitted sections by index.
    //   2. Inserts: append user-created sections, then sort everything by
    //      startTime so the timeline reads in chronological order.
    const fmt = (t: number) =>
      `${Math.floor(t / 60)}:${Math.floor(t % 60).toString().padStart(2, '0')}`;
    const buildLabel = (type: SectionType): string => {
      const names: Record<SectionType, string> = {
        'mix-in': 'Mix In', 'intro': 'Intro', 'breakdown': 'Breakdown',
        'main-drop': 'Drop', 'bridge': 'Bridge', 'outro': 'Outro',
        'mix-out': 'Mix Out', 'verse': 'Verse', 'chorus': 'Chorus',
        'build': 'Build',
      };
      return names[type];
    };
    const updated: Section[] = classified.map((s, idx) => {
      const o = overrides[idx];
      // ALWAYS carry classifierIndex through — the editor uses it to write
      // back to the correct override slot even after sort-by-startTime
      // reorders the merged list.
      const base = { ...s, classifierIndex: idx };
      if (!o) return base;
      const merged: Section = {
        ...base,
        ...(o.type ? { type: o.type, label: '' } : {}),
        ...(o.startTime !== undefined ? { startTime: o.startTime } : {}),
        ...(o.endTime !== undefined ? { endTime: o.endTime } : {}),
      };
      if (o.startTime !== undefined || o.endTime !== undefined) {
        merged.timestamp = `${fmt(merged.startTime)} - ${fmt(merged.endTime)}`;
      }
      return merged;
    });

    // Materialize inserts as full Section objects, tagged with insertId so
    // the editor knows to route commits/delete through the insert paths.
    const insertedSections: Section[] = inserts.map((ins) => ({
      type: ins.type,
      label: buildLabel(ins.type),
      startTime: ins.startTime,
      endTime: ins.endTime,
      segments: [],
      confidence: 1,
      avgEnergy: 0,
      timestamp: `${fmt(ins.startTime)} - ${fmt(ins.endTime)}`,
      insertId: ins.insertId,
    }));

    return [...updated, ...insertedSections].sort(
      (a, b) => a.startTime - b.startTime,
    );
  }, [rawSegments, audioPeaks, analysis?.durationSeconds, analysis?.genre?.family, overrides, inserts]);

  useEffect(() => {
    if (!analysisId) return;

    const fetchAnalysis = async () => {
      try {
        const result = await getAnalysis(analysisId);
        
        // Calculate duration from segments if not provided
        if (!result.durationSeconds && result.segments && result.segments.length > 0) {
          const lastSegment = result.segments[result.segments.length - 1];
          const calculatedDuration = lastSegment.endTimeSeconds || lastSegment.endTime || 0;
          if (calculatedDuration > 0) {
            result.durationSeconds = calculatedDuration;
            console.log('Calculated duration from segments:', calculatedDuration);
          }
        }
        
        // Log experimental features if present
        if (result.experimental) {
          console.log('🔬 Experimental Features Detected:', result.experimental);
          if (result.experimental.embedding_v1) {
            console.log(`  📊 TorchAudio Embedding: ${result.experimental.embedding_v1.length} dimensions (v${result.experimental.embedding_version || '1'})`);
          }
          if (result.experimental.audioflux_feature_set_v1) {
            console.log(`  🎵 AudioFlux Features: ${Object.keys(result.experimental.audioflux_feature_set_v1).length} features (v${result.experimental.audioflux_version || '1'})`);
          }
        }
        // Experimental features (TorchAudio, AudioFlux) are computed but hidden from UI
        // They're stored in the database for backend use (similarity matching, AI features)
        
        setAnalysis(result);
        setLoading(false);

        // Get audio file URL for playback
        if (result.fileUrl) {
          setAudioUrl(result.fileUrl);
        }

        // Classify segments into musical sections
        if (result.segments.length > 0) {
          const segmentData = result.segments.map(s => ({
            segmentNumber: s.segmentNumber,
            startTime: s.startTimeSeconds ?? s.startTime,
            endTime: s.endTimeSeconds ?? s.endTime,
            energy: s.energy,
            chordProgression: s.chordProgression,
            key: s.key,
            instruments: s.instruments,
            mixing: s.mixing,
          }));
          // Hand the raw segments to state — the useMemo above re-classifies
          // them when the high-res audio peak series arrives.
          setRawSegments(segmentData);
        }

        // Poll for updates if still processing
        if (result.status === 'processing') {
          const interval = setInterval(async () => {
            try {
              const status = await getAnalysisStatus(analysisId);
              if (status.status === 'completed') {
                const updated = await getAnalysis(analysisId);
                setAnalysis(updated);
                if (updated.fileUrl) {
                  setAudioUrl(updated.fileUrl);
                }
                // Re-classify sections (useMemo handles the re-run)
                if (updated.segments.length > 0) {
                  const segmentData = updated.segments.map(s => ({
                    segmentNumber: s.segmentNumber,
                    startTime: s.startTimeSeconds ?? s.startTime,
                    endTime: s.endTimeSeconds ?? s.endTime,
                    energy: s.energy,
                    chordProgression: s.chordProgression,
                    key: s.key,
                    instruments: s.instruments,
                    mixing: s.mixing,
                  }));
                  setRawSegments(segmentData);
                }
                clearInterval(interval);
              } else if (status.status === 'failed') {
                setError('Analysis failed. Please try uploading again.');
                clearInterval(interval);
              }
            } catch (err: any) {
              // Only log if it's not a network error (might be temporary)
              if (err.code !== 'ERR_NETWORK') {
                console.error('Status check error:', err);
              }
            }
          }, 2000);

          return () => clearInterval(interval);
        }
      } catch (err: any) {
        setError(err.response?.data?.error || err.message || 'Failed to load analysis');
        setLoading(false);
      }
    };

    fetchAnalysis();
  }, [analysisId]);

  const handleSeek = (time: number) => {
    setCurrentTime(time);
  };

  const handlePlaySection = (time: number) => {
    setCurrentTime(time);
    setPlaySignal((n) => n + 1);
  };

  if (loading) {
    return (
      <div className="results-page">
        <div className="container">
          <div className="loading-state">
            <LoadingSpinner size="lg" color="cyan" />
            <p>Loading analysis...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="results-page">
        <div className="container">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return null;
  }

  // Extract track name and artist from filename
  const getTrackInfo = (filename?: string): { trackName: string; artist: string; artists: string[] } => {
    if (!filename) return { trackName: 'Unknown Track', artist: 'Unknown Artist', artists: [] };
    
    // Try to parse "Artist - Track Name" or "Artist, Artist2 - Track Name"
    const match = filename.match(/^(.+?)\s*-\s*(.+?)(?:\s*\(.*?\))?\.(mp3|wav|flac|m4a|aac)$/i);
    if (match) {
      const artists = match[1].split(',').map(a => a.trim());
      const trackName = match[2].trim();
      return {
        trackName,
        artist: artists.join(', '),
        artists: artists,
      };
    }
    
    // Fallback: just filename without extension
    const nameWithoutExt = filename.replace(/\.(mp3|wav|flac|m4a|aac)$/i, '');
    return { trackName: nameWithoutExt, artist: 'Unknown Artist', artists: [] };
  };

  const trackInfo = getTrackInfo(analysis.filename);

  return (
    <div className="results-page">
      <div className="container">
        {/* Row 1 — view-mode toggle on its own line, right-aligned. */}
        <div className="results-top-bar">
          <div className="view-mode-toggle" role="tablist" aria-label="Analysis detail level">
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === 'compact'}
              className={`view-mode-pill view-mode-pill-compact ${viewMode === 'compact' ? 'is-active' : ''}`}
              onClick={() => setViewMode('compact')}
            >
              Compact
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === 'expanded'}
              aria-disabled={isNarrow}
              disabled={isNarrow}
              className={`view-mode-pill view-mode-pill-expanded ${viewMode === 'expanded' ? 'is-active' : ''} ${isNarrow ? 'is-disabled' : ''}`}
              onClick={() => setViewMode('expanded')}
              title={isNarrow ? 'Expanded view is available on desktop' : ''}
            >
              Expanded
              {isNarrow && <span className="view-mode-pill-badge">Desktop only</span>}
            </button>
          </div>
        </div>

        {/* Row 2 — track hero: name + artist, typography only (no card chrome). */}
        <header className="track-hero">
          <h1 className="track-hero-name">{trackInfo.trackName}</h1>
          <p className="track-hero-artist">{trackInfo.artist}</p>
        </header>
          

        {/* Waveform Visualization */}
        {audioUrl && (analysis.durationSeconds || (analysis.segments && analysis.segments.length > 0)) ? (
          <div className="waveform-section">
            <Waveform
              audioUrl={audioUrl}
              duration={analysis.durationSeconds || (analysis.segments && analysis.segments.length > 0 
                ? Math.max(...analysis.segments.map(s => s.endTime || s.endTimeSeconds || 0))
                : 0)}
              segments={sections.map((s, idx) => ({
                id: `section-${idx}`,
                segmentNumber: idx + 1,
                startTime: s.startTime,
                endTime: s.endTime,
                chordProgression: s.chordProgression,
                key: s.dominantKey,
                energy: s.avgEnergy,
                sectionType: s.type,
                timestamp: s.timestamp,
              }))}
              currentTime={currentTime}
              onSeek={handleSeek}
              loopRegion={loopRegion}
            />
          </div>
        ) : null}

        {/* Audio Player */}
        {audioUrl && (
          <div className="player-section">
            <AudioPlayer
              audioUrl={audioUrl}
              filename={analysis.filename || 'Audio file'}
              currentTime={currentTime}
              onSeek={handleSeek}
              onLoopRegionChange={setLoopRegion}
              tempo={analysis.tempo}
              playSignal={playSignal}
            />
          </div>
        )}

        {/* Track metrics — BPM, key, Camelot, chord progression. Sits BELOW the
            player as part of the "track details" row the user spec'd. */}
        {(analysis.tempo || analysis.key || (analysis.chordProgression && analysis.chordProgression.length > 0)) && (
          <div className="track-metrics-strip">
            <div className="metric-card metric-card-bpm">
              <div className="info-label">BPM</div>
              <div className="info-value">{analysis.tempo ? Math.round(analysis.tempo) : '—'}</div>
            </div>
            <div className="metric-card metric-card-key">
              <div className="info-label">KEY</div>
              <div className="info-value">{analysis.key || '—'}</div>
            </div>
            {analysis.key && toCamelot(analysis.key) && (
              <div className="metric-card metric-card-camelot">
                <div className="info-label">CAMELOT</div>
                <div className="info-value">{toCamelot(analysis.key)}</div>
              </div>
            )}
            {analysis.chordProgression && analysis.chordProgression.length > 0 && (
              <div className="metric-card metric-card-chords">
                <div className="info-label">CHORD PROGRESSION</div>
                <div className="info-value chord-progression">{analysis.chordProgression.join(' → ')}</div>
              </div>
            )}
          </div>
        )}

        {/* Detail cards — advanced features, genre, audio insights (expanded). */}
        <TrackDetailCards analysis={analysis} viewMode={viewMode} />

        {/* Processing Notice — full AI loader + rotating trivia + multi-step loader */}
        {analysis.status === 'processing' && (
          <div className="processing-notice">
            <div className="processing-loader-wrap">
              <AILoader text="Analyzing" size={220} />
            </div>
            <div className="processing-content">
              <p className="processing-eyebrow">
                <span className="processing-dot" /> Live · AI Track Analysis
              </p>
              <h3>Analyzing your track</h3>
              <p className="processing-sub">
                This typically takes 1–3 minutes. Sit tight while we listen
                end-to-end and turn your track into a full musical breakdown.
              </p>
              <MultiStepLoader
                loading={analysis.status === 'processing'}
                stepDurationMs={8000}
                steps={[
                  'Decoding audio and detecting beat grid',
                  'Detecting musical sections',
                  'Analyzing BPM, key, and chord progressions',
                  'Identifying instruments and mixing',
                  'Predicting genre',
                  'Generating insights',
                ]}
              />
              <AnalysisTrivia />
            </div>
          </div>
        )}

        {/* Musical Sections Overview */}
        {sections.length > 0 && (
          <div className="segments-overview">
            <h2 className="section-title">Track Structure</h2>
            <div className="segments-timeline">
              {sections.map((section, idx) => {
                const startTime = section.startTime;
                // Close any gap between this section and the next so the
                // timeline tiles edge-to-edge. Last section extends to the
                // track's full duration.
                const next = sections[idx + 1];
                const totalDur = analysis.durationSeconds || 1;
                // Last section ALWAYS extends to track end so the row tiles
                // to the right edge — never a half-empty tail.
                const endTime = next ? next.startTime : totalDur;
                const glyph = SECTION_GLYPH[section.type] ?? '·';
                return (
                  <button
                    type="button"
                    key={idx}
                    className={`section-marker section-marker-${section.type}`}
                    style={{
                      left: `${(startTime / totalDur) * 100}%`,
                      width: `${((endTime - startTime) / totalDur) * 100}%`,
                    }}
                    title={`${getSectionLabel(section)} · ${section.timestamp} · click to play`}
                    onClick={() => handlePlaySection(startTime)}
                  >
                    <div className="section-label">
                      <span className="section-label-badge" aria-hidden="true">{glyph}</span>
                      <span className="section-label-text">{getSectionLabel(section)}</span>
                    </div>
                    <div className="section-time">{section.timestamp}</div>
                  </button>
                );
              })}
            </div>

            {viewMode === 'compact' ? (
              /* Compact / Pro view — single Song Snapshot card, no per-section
                 detail, no override flow. */
              <SongSnapshot
                analysis={analysis}
                sections={sections}
                onSeek={handlePlaySection}
              />
            ) : (
              <>
                {/* Single CTA below the timeline — opens the SectionEditorModal
                    where the user can play each section, correct any wrong
                    labels, and the classifier learns from those corrections. */}
                <div className="section-correct-bar">
                  <button
                    type="button"
                    className="section-correct-cta"
                    onClick={() => setEditorOpen(true)}
                  >
                    <Pencil size={12} strokeWidth={2.2} />
                    <span>Correct sections</span>
                  </button>
                  <span className="section-correct-hint">
                    Got a section wrong? Tag the right type — every correction
                    trains the classifier.
                  </span>
                </div>

                {/* Section-Based Insights */}
                <div className="sections-insights">
                  <h2 className="section-title">Musical Analysis by Section</h2>
                  {sections.map((section, idx) => (
                    <SectionInsights
                      key={idx}
                      section={section}
                      sectionIndex={idx}
                      totalSections={sections.length}
                      analysisId={analysis.id}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Editor modal — fixed-positioned, mounts only when open. */}
        <SectionEditorModal
          open={editorOpen}
          onClose={() => setEditorOpen(false)}
          audioUrl={audioUrl}
          sections={sections}
          duration={analysis?.durationSeconds ?? audioPeaks?.duration ?? 0}
          onSubmitType={submitType}
          onSubmitBoundary={submitBoundary}
          onSubmitInsert={submitInsert}
          onEditInsert={editInsert}
          onRemoveInsert={removeInsert}
          onClearAll={clearAll}
        />

        {/* DJ workflow — Camelot/BPM-matched mix suggestions from your library. */}
        {analysis.status === 'completed' && (analysis.key || analysis.tempo) && (
          <DJSuggestions
            currentAnalysisId={analysis.id}
            currentKey={analysis.key}
            currentBpm={analysis.tempo}
          />
        )}

        {/* AI Chat for Deeper Analysis */}
        {analysis.status === 'completed' && sections.length > 0 && (
          <AIChat
            analysis={analysis}
            sections={sections}
            trackInfo={{
              trackName: trackInfo.trackName,
              artist: trackInfo.artist,
              artists: trackInfo.artists || [],
            }}
          />
        )}

        {/* Route the fan to a bookable KNKT expert once the analysis is
            complete. Copy positions the human as adding taste on top of
            volume — not replacing the AI. */}
        {analysis.status === 'completed' && (
          <aside className="expert-cta-section" aria-label="Talk to a KNKT expert">
            <div className="expert-cta-row">
              <div className="expert-cta-copy">
                <span className="expert-cta-label">Want a taste-call?</span>
                <span className="expert-cta-body">
                  Get specific advice on this track from a top KNKT artist.
                </span>
              </div>
              <a
                className="expert-cta-button"
                href={`https://book.knkt.io/browse?utm_source=audioagent&utm_medium=results_page&trackName=${encodeURIComponent(
                  trackInfo.trackName || ''
                )}&artist=${encodeURIComponent(trackInfo.artist || '')}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Talk to a KNKT expert
                <span aria-hidden="true">→</span>
              </a>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
